<div data-testid="confirm-shipping-page" class="app-confirm-shipping">
  <!-- Sticky Header -->
  <div class="sticky-header">
    <div class="flex justify-between items-center mb-5">
      <div class="flex items-center gap-3">
        <h2 class="text-2xl font-semibold text-gray-800">
          {{ "outbound_management.confirm_shipping.title" | translate }}
        </h2>
        @if (loading()) {
          <i class="pi pi-spin pi-spinner text-xl text-gray-600"></i>
        }
      </div>
      <div class="flex gap-3">
        <app-button
          [testId]="'confirm-shipping-cancel-btn'"
          [label]="'common.actions.cancel' | translate"
          (emitOnClick)="onBackClick()"
          [outlined]="true"
          [type]="'secondary'"
        ></app-button>
        <app-button
          [testId]="'confirm-shipping-confirm-btn'"
          [label]="'outbound_management.actions.confirm_shipping' | translate"
          (emitOnClick)="onConfirmShipping()"
          [type]="'info'"
          [loading]="saving()"
          [disabled]="isConfirmDisabled() || saving()"
        ></app-button>
      </div>
    </div>
  </div>

  <!-- Content -->
  <div class="confirm-shipping-content">
    @if (loading()) {
      <div class="flex justify-center items-center py-8">
        <i class="pi pi-spin pi-spinner text-2xl"></i>
      </div>
    } @else {
      @if (formData()) {
        <form data-testid="confirm-shipping-form" [formGroup]="confirmShippingForm" class="space-y-6">
          <!-- Basic Information Section -->
          <p-card styleClass="shadow-sm border-0">
            <ng-template pTemplate="header">
              <div class="flex items-center">
                <h2 class="text-lg font-medium text-gray-800">
                  {{
                    "outbound_management.sections.basic_information" | translate
                  }}
                </h2>
              </div>
            </ng-template>
            <ng-template pTemplate="content">
              <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div class="space-y-2">
                  <span class="text-sm font-semibold text-gray-700">
                    {{ "outbound_management.fields.outbound_type" | translate }}
                  </span>
                  <p class="text-sm text-gray-800">
                    {{ getFieldValue("outTypeMeaning") || "-" }}
                  </p>
                </div>
                <div class="space-y-2">
                  <span class="text-sm font-semibold text-gray-700">
                    {{
                      "outbound_management.fields.outbound_status" | translate
                    }}
                  </span>
                  <div>
                    <p-tag
                      data-testid="confirm-shipping-status-tag"
                      [value]="getStatusLabel(getFieldValue('status'))"
                      [severity]="getStatusSeverity(getFieldValue('status'))"
                      styleClass="status-tag"
                    />
                  </div>
                </div>
                <div class="space-y-2">
                  <span class="text-sm font-semibold text-gray-700">
                    {{
                      "outbound_management.fields.reference_document"
                        | translate
                    }}
                  </span>
                  <p class="text-sm text-gray-800">
                    {{ getFieldValue("frontRecordCode") || "-" }}
                  </p>
                </div>
                <div class="space-y-2">
                  <span class="text-sm font-semibold text-gray-700">
                    {{
                      "outbound_management.table.columns.creation_time"
                        | translate
                    }}
                  </span>
                  <p class="text-sm text-gray-800">
                    {{ formatDate(getFieldValue("creationDate")) || "-" }}
                  </p>
                </div>
              </div>
            </ng-template>
          </p-card>

          <!-- Warehouse and Logistics Information Section -->
          <p-card styleClass="shadow-sm border-0">
            <ng-template pTemplate="header">
              <div class="flex items-center">
                <h2 class="text-lg font-medium text-gray-800">
                  {{
                    "outbound_management.sections.warehouse_information"
                      | translate
                  }}
                </h2>
              </div>
            </ng-template>
            <ng-template pTemplate="content">
              <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div class="space-y-2">
                  <span class="text-sm font-semibold text-gray-700">
                    {{
                      "outbound_management.fields.outbound_warehouse"
                        | translate
                    }}
                  </span>
                  <p class="text-sm text-gray-800">
                    {{
                      getWarehouseDisplay(
                        "outLogicWarehouseCode",
                        "outLogicWarehouseName"
                      ) || "-"
                    }}
                  </p>
                </div>
                <div class="space-y-2">
                  <span class="text-sm font-semibold text-gray-700">
                    {{
                      "outbound_management.fields.receiving_warehouse"
                        | translate
                    }}
                  </span>
                  <p class="text-sm text-gray-800">
                    {{
                      getWarehouseDisplay(
                        "receivingLogicWarehouseCode",
                        "receivingLogicWarehouseName"
                      ) || "-"
                    }}
                  </p>
                </div>
                <div class="space-y-2">
                  <span class="text-sm font-semibold text-gray-700">
                    {{
                      "outbound_management.fields.logistics_company" | translate
                    }}
                  </span>
                  <p class="text-sm text-gray-800">
                    {{ getFieldValue("logisticCompany") || "-" }}
                  </p>
                </div>
                <div class="space-y-2">
                  <span class="text-sm font-semibold text-gray-700">
                    {{
                      "outbound_management.fields.logistics_business"
                        | translate
                    }}
                  </span>
                  <p class="text-sm text-gray-800">
                    {{ getFieldValue("logisticBusiness") || "-" }}
                  </p>
                </div>
              </div>
            </ng-template>
          </p-card>

          <!-- Confirm Shipping Form Section -->
          <p-card styleClass="shadow-sm border-0">
            <ng-template pTemplate="header">
              <div class="flex items-center">
                <h2 class="text-lg font-medium text-gray-800">
                  {{
                    "outbound_management.confirm_shipping.form_title"
                      | translate
                  }}
                </h2>
              </div>
            </ng-template>
            <ng-template pTemplate="content">
              <div
                class="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-end"
              >
                <div
                  class="flex flex-col sm:flex-row sm:items-center gap-2 md:col-span-3"
                >
                  <label
                    for="cs-shipping-no"
                    class="text-sm font-semibold text-gray-700 whitespace-nowrap"
                  >
                    {{ "outbound_management.fields.shipping_no" | translate }}:
                  </label>
                  <input
                    data-testid="confirm-shipping-shipping-no-input"
                    type="text"
                    id="cs-shipping-no"
                    formControlName="shippingNo"
                    class="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400"
                    [placeholder]="
                      'outbound_management.placeholders.enter_shipping_no'
                        | translate
                    "
                  />
                </div>
                <div
                  class="flex flex-col sm:flex-row sm:items-center gap-2 md:col-span-3"
                >
                  <label
                    for="cs-notice-code"
                    class="text-sm font-semibold text-gray-700 whitespace-nowrap"
                  >
                    {{
                      "outbound_management.fields.real_notice_code" | translate
                    }}:
                  </label>
                  <input
                    data-testid="confirm-shipping-notice-code-input"
                    type="text"
                    id="cs-notice-code"
                    formControlName="realNoticeCode"
                    class="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400"
                    [placeholder]="
                      'outbound_management.placeholders.enter_real_notice_code'
                        | translate
                    "
                  />
                </div>
                <div
                  class="flex flex-col sm:flex-row sm:items-center gap-2 md:col-span-6"
                >
                  <span
                    class="text-sm font-semibold text-gray-700 whitespace-nowrap"
                  >
                    {{
                      "outbound_management.fields.scheduled_delivery_time"
                        | translate
                    }}:
                  </span>
                  <div class="flex-1 datepicker-wrapper delivery-date-input">
                    <app-advanced-datepicker
                      data-testid="confirm-shipping-delivery-date-input"
                      formControlName="deliveryDate"
                      [placeholder]="
                        'outbound_management.placeholders.select_delivery_date'
                          | translate
                      "
                      [minDate]="minDeliveryDate"
                      (dateChange)="onDeliveryDateChange($event)"
                      styleClass="z-20"
                    ></app-advanced-datepicker>
                  </div>
                </div>
              </div>
              <!-- Item Details -->
              @if (productDetails().length > 0) {
                <div class="mb-6 mb-4 mt-4">
                  <div class="text-sm font-semibold text-gray-700 pb-2">
                    {{
                      "outbound_management.sections.item_details" | translate
                    }}
                  </div>
                  <app-data-table
                    data-testid="confirm-shipping-product-table"
                    [dataSource]="productDetails()"
                    [columns]="productTableColumns()"
                    [loading]="false"
                    [totalRecords]="productDetails().length"
                    [rows]="productDetails().length"
                    [hasCheckColumn]="false"
                    [hasHeaderCheckbox]="false"
                    [hasSerialColumn]="false"
                  ></app-data-table>
                </div>
              }
            </ng-template>
          </p-card>
        </form>
      }
    }
  </div>
</div>

<!-- Toast Messages -->
<p-toast data-testid="confirm-shipping-toast"></p-toast>
